import { useState, useCallback } from 'react';

/**
 * Custom hook for managing write-in card creation
 * Handles write-in state, modal controls, and card creation logic
 */
const useWriteInCards = (
  socketEmit,
  currentPlayer,
  currentBlackCard,
  addToast,
  refetchWriteInStats,
  trackFeature,
  writeInStats,
  writeInsEnabled
) => {
  const [writeInCards, setWriteInCards] = useState([]);
  const [writeInText, setWriteInText] = useState('');
  const [showWriteInModal, setShowWriteInModal] = useState(false);

  const openWriteInModal = useCallback(() => {
    setShowWriteInModal(true);
    setWriteInText('');
  }, []);

  const closeWriteInModal = useCallback(() => {
    setShowWriteInModal(false);
    setWriteInText('');
  }, []);

  const createWriteInCard = useCallback(() => {
    const trimmedText = writeInText.trim();

    if (!writeInsEnabled) {
      console.error('Write-in cards are disabled in this room');
      return;
    }

    if (!trimmedText) {
      console.error('Write-in card cannot be empty');
      return;
    }

    if (trimmedText.length > 200) {
      console.error('Write-in card must be 200 characters or less');
      return;
    }

    // Check if we're at the limit
    const currentPickCount = currentBlackCard?.pick || 1;
    const maxWriteIns = Math.min(2, currentPickCount);

    if (writeInCards.length >= maxWriteIns) {
      console.error(`Maximum ${maxWriteIns} write-in cards allowed`);
      return;
    }

    // Create new write-in card object with proper backend structure
    const newWriteInCard = {
      id: `writein_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: trimmedText,
      isWriteIn: true,
    };

    setWriteInCards((prev) => [...prev, newWriteInCard]);
    setShowWriteInModal(false);
    setWriteInText('');

    console.log('✍️ Created write-in card:', newWriteInCard);

    // Show success toast and refetch stats
    addToast('Write-in card created successfully', 'success', 2000);
    refetchWriteInStats();

    return newWriteInCard;
  }, [
    writeInText,
    writeInsEnabled,
    currentBlackCard,
    writeInCards.length,
    addToast,
    refetchWriteInStats,
  ]);

  const removeWriteInCard = useCallback((cardId) => {
    setWriteInCards((prev) => prev.filter((card) => card.id !== cardId));
    console.log('🗑️ Removed write-in card:', cardId);
  }, []);

  return {
    writeInCards,
    setWriteInCards,
    writeInText,
    setWriteInText,
    showWriteInModal,
    openWriteInModal,
    closeWriteInModal,
    createWriteInCard,
    removeWriteInCard,
  };
};

export default useWriteInCards;
